import {
  ApolloClient,
  concat,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { GraphQLError, GraphQLFormattedError } from "graphql";

import {
  addTruthyParamsToUrl,
  extractParams,
  urlWithNext,
} from "@zapier/url-utils";
import { filterOnAllKeys, limitOffsetPagination } from "./paginationHelpers";

export interface CreateApolloClientOptions {
  concatZapRuns?: boolean;
}

function redirectToLogin() {
  if (typeof window !== "undefined") {
    const reauthUrl = addTruthyParamsToUrl(
      urlWithNext("/app/login", window.location.pathname),
      {
        reauth: true,
      }
    );
    window.location.href = reauthUrl;
  }
}

function hasRealmMismatchError(
  graphQLErrors?: readonly GraphQLFormattedError[]
) {
  if (!Array.isArray(graphQLErrors)) {
    return false;
  }

  const isRealmMismatch = (error: GraphQLError) =>
    error.extensions.code === 403 &&
    (error.extensions.exception as any)?.response?.body?.reason ===
      "invalid_auth_realm";

  return graphQLErrors.some(isRealmMismatch);
}

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (networkError) {
    console.warn(`[Network error]: ${networkError}`);
  }

  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) => {
      console.warn(`[GraphQL error] ${message}`, {
        message,
        locations,
        path,
      });
    });
  }

  if (
    (networkError &&
      "statusCode" in networkError &&
      networkError.statusCode === 401) ||
    (graphQLErrors && graphQLErrors?.[0]?.extensions?.code === 401)
  ) {
    // If we received a 401 the user needs to log in
    redirectToLogin();
  }

  // Check if there was an auth-realm violation (SAML vs regular credentials)
  if (hasRealmMismatchError(graphQLErrors)) {
    if (typeof window !== "undefined") {
      const params = extractParams(window.location.href);

      // We only want to dispatch this route if
      // we aren't already in a reauth flow (otherwise we could
      // potentially trigger this multiple times)
      if (!params.reauth) {
        // There was a violation and the user isn't in the auth flow
        // so force the user to reauthenticate
        redirectToLogin();
      }
    }
  }
});

function createApolloClient(options: CreateApolloClientOptions = {}) {
  // HttpLink to our local graphql BFF
  const localGqlLink = new HttpLink({
    credentials: "include",
    uri: "/api/reporting/graphql",
  });

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        Flags: {
          merge: true,
        },

        // Enable partial Zap Run data to be cached between the Zap runs page and Run details fe
        Query: {
          fields: {
            zapRun: {
              read(_existing, { args, toReference }) {
                return toReference({
                  __typename: "ZapRun",
                  id: args?.id,
                });
              },
            },

            // If using the new pagination experience, we merge results across pages.
            ...(options.concatZapRuns && {
              zapRuns: limitOffsetPagination(filterOnAllKeys()),
            }),
          },
        },
      },
    }),
    link: concat(errorLink, localGqlLink),
    name: "reporting",
  });
}

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;
let optionsCache: string = "";

function testOptionsChanged(newOptions: CreateApolloClientOptions) {
  const stringifiedOptions = JSON.stringify(newOptions);
  if (optionsCache !== stringifiedOptions) {
    optionsCache = stringifiedOptions;
    return true;
  }

  return false;
}

export function getOrCreateApolloClient(
  options: CreateApolloClientOptions = {}
) {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (typeof window === "undefined") {
    return createApolloClient(options);
  }

  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = createApolloClient(options);
  }

  // If the options have changed, we need to recreate the client
  if (testOptionsChanged(options)) {
    apolloClient = createApolloClient(options);
  }

  return apolloClient;
}
